export interface ThemeColors {
  bg: number;
  bgDark: number;
  bgPanel: number;
  cyan: number;
  blue: number;
  purple: number;
  pink: number;
  red: number;
  green: number;
  yellow: number;
  white: number;
  gray: number;
  darkGray: number;
  accent: number;
  text: string;
  textDim: string;
}

export const THEMES: Record<string, ThemeColors> = {
  cyberpunk: {
    bg: 0x0a0a1a, bgDark: 0x050510, bgPanel: 0x111133,
    cyan: 0x00f0ff, blue: 0x1a8fff, purple: 0x9945ff,
    pink: 0xff2d7b, red: 0xff1744, green: 0x00e676,
    yellow: 0xffea00, white: 0xffffff, gray: 0x555577,
    darkGray: 0x222244, accent: 0x00f0ff,
    text: '#ffffff', textDim: '#555577',
  },
  light: {
    bg: 0xf0f0ff, bgDark: 0xe0e0f0, bgPanel: 0xffffff,
    cyan: 0x0088cc, blue: 0x0055aa, purple: 0x7733cc,
    pink: 0xcc2266, red: 0xcc1133, green: 0x00aa55,
    yellow: 0xccaa00, white: 0x222222, gray: 0x8888aa,
    darkGray: 0xccccdd, accent: 0x0088cc,
    text: '#222222', textDim: '#888888',
  },
  dark: {
    bg: 0x111122, bgDark: 0x0a0a15, bgPanel: 0x1a1a2e,
    cyan: 0x22ccdd, blue: 0x2277cc, purple: 0x7733aa,
    pink: 0xcc2255, red: 0xcc1122, green: 0x22aa55,
    yellow: 0xccaa22, white: 0xcccccc, gray: 0x444466,
    darkGray: 0x222233, accent: 0x22ccdd,
    text: '#cccccc', textDim: '#444466',
  },
};

const themeKey = localStorage.getItem('neuro_duel_theme') || 'cyberpunk';
export const COLORS: ThemeColors = THEMES[themeKey] || THEMES.cyberpunk;
export const CURRENT_THEME = themeKey;

export function setTheme(key: string) {
  localStorage.setItem('neuro_duel_theme', key);
  const t = THEMES[key];
  if (t) {
    Object.assign(COLORS, t);
  }
  // Update CSS variables
  document.documentElement.style.setProperty('--bg', intToHex(COLORS.bg));
  document.documentElement.style.setProperty('--cyan', intToHex(COLORS.cyan));
}

export function intToHex(color: number): string {
  return '#' + color.toString(16).padStart(6, '0');
}

export const GAME_W = 1280;
export const GAME_H = 720;

// Player constants
export const PLAYER_SPEED = 280;
export const PLAYER_JUMP_VEL = -550;
export const PLAYER_DODGE_SPEED = 600;
export const PLAYER_DODGE_DURATION = 300;
export const PLAYER_SHIELD_DURATION = 2000;
export const PLAYER_SHIELD_COOLDOWN = 5000;
export const PLAYER_DASH_SPEED = 700;
export const PLAYER_DASH_DURATION = 200;
export const PLAYER_DASH_COOLDOWN = 4000;
export const PLAYER_ATTACK_COOLDOWN = 350;
export const PLAYER_HEAVY_ATTACK_COOLDOWN = 1200;
export const PLAYER_SKILL_COOLDOWN = 3000;

export interface PlayerStats {
  hitsLanded: number;
  hitsMissed: number;
  dodges: number;
  damagesTaken: number;
  combo: number;
  maxCombo: number;
  shieldUses: number;
  skillUses: number;
  heavyAttacks: number;
  headTrackingAccuracy: number;
  headMovements: number;
  headHits: number;
}

export function createPlayerStats(): PlayerStats {
  return {
    hitsLanded: 0, hitsMissed: 0, dodges: 0, damagesTaken: 0,
    combo: 0, maxCombo: 0, shieldUses: 0, skillUses: 0,
    heavyAttacks: 0, headTrackingAccuracy: 0, headMovements: 0, headHits: 0,
  };
}

export type ChallengeType = 'color' | 'arrow' | 'number';

export interface ChallengeEvent {
  type: ChallengeType;
  value: string;
  x: number;
  y: number;
  duration: number;
  createdAt: number;
}

export type BossPhase = 1 | 2 | 3;

export interface BossAIRecord {
  playerMovementDirection: string;
  attackFrequency: number;
  skillUsage: number;
  dodgeCount: number;
  damageTaken: number;
  playerDamageDealt: number;
  leftDodges: number;
  rightDodges: number;
  aggressiveMoves: number;
  defensiveMoves: number;
}

export interface LeaderboardEntry {
  playerName?: string;
  score: number;
  grade: string;
  battleTime: number;
  bossDamage: number;
  hitRate: number;
  maxCombo: number;
  dodgeCount: number;
  challengeCompletionRate: number;
  timestamp: number;
}
